import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Receipt, Plus, Trash2, Download, CheckCircle2, XCircle, Clock,
  Edit, Eye, Filter, BarChart3, Upload, FileText, AlertTriangle
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { agencyExpensesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const STATUS_BADGES = {
  pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  validated: { label: 'Valide', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  rejected: { label: 'Rejete', cls: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
};

const fmtMoney = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-FR')} FCFA`;
const fmtDate = (s) => {
  if (!s) return '-';
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  return isNaN(d) ? s : format(d, 'dd MMM yyyy', { locale: fr });
};

const EMPTY_FORM = {
  expense_date: new Date().toISOString().slice(0, 10),
  category: 'fournitures_bureau',
  description: '',
  amount: '',
  payment_method: 'especes',
  supplier: '',
  notes: '',
};

export default function AgencyExpenses() {
  const { user } = useAuth();
  const isAdmin = user && (['admin', 'super_admin', 'agent'].includes(user.role));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterMonth, setFilterMonth] = useState('');
  const [stats, setStats] = useState(null);
  const [showStats, setShowStats] = useState(false);

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(null);

  const [showDetail, setShowDetail] = useState(false);
  const [detail, setDetail] = useState(null);

  const [showReject, setShowReject] = useState(false);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const loadCategories = useCallback(async () => {
    try {
      const r = await agencyExpensesAPI.getCategories();
      setCategories(r.data.categories || []);
      setPaymentMethods(r.data.payment_methods || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterMonth) params.month = filterMonth;
      const r = await agencyExpensesAPI.getAll(params);
      setItems(r.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Erreur chargement frais');
    }
    setLoading(false);
  }, [filterStatus, filterCategory, filterMonth]);

  const loadStats = useCallback(async () => {
    try {
      const year = filterMonth ? filterMonth.slice(0, 4) : null;
      const r = await agencyExpensesAPI.getStats(year);
      setStats(r.data);
    } catch (e) {
      console.error(e);
    }
  }, [filterMonth]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const totals = useMemo(() => {
    const t = items.reduce((acc, i) => {
      acc.all += i.amount || 0;
      acc[i.status] = (acc[i.status] || 0) + (i.amount || 0);
      return acc;
    }, { all: 0, pending: 0, validated: 0, rejected: 0 });
    return t;
  }, [items]);

  const handleCreate = async () => {
    if (!form.description.trim() || !form.amount || Number(form.amount) <= 0) {
      toast.error('Description et montant valide requis');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('expense_date', form.expense_date);
      fd.append('category', form.category);
      fd.append('description', form.description);
      fd.append('amount', String(form.amount));
      fd.append('payment_method', form.payment_method);
      if (form.supplier) fd.append('supplier', form.supplier);
      if (form.notes) fd.append('notes', form.notes);
      if (invoiceFile) fd.append('invoice', invoiceFile);
      await agencyExpensesAPI.create(fd);
      toast.success('Frais cree');
      setShowCreate(false);
      setForm(EMPTY_FORM);
      setInvoiceFile(null);
      await Promise.all([loadItems(), loadStats()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur creation');
    }
    setSubmitting(false);
  };

  const handleEditSave = async () => {
    if (!editing) return;
    setSubmitting(true);
    try {
      await agencyExpensesAPI.update(editing.id, {
        expense_date: editing.expense_date,
        category: editing.category,
        description: editing.description,
        amount: Number(editing.amount),
        payment_method: editing.payment_method,
        supplier: editing.supplier || null,
        notes: editing.notes || null,
      });
      toast.success('Frais modifie');
      setShowEdit(false);
      setEditing(null);
      await Promise.all([loadItems(), loadStats()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur modification');
    }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer definitivement ce frais ?')) return;
    try {
      await agencyExpensesAPI.delete(id);
      toast.success('Frais supprime');
      await Promise.all([loadItems(), loadStats()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleValidate = async (id) => {
    try {
      await agencyExpensesAPI.validate(id);
      toast.success('Frais valide');
      await Promise.all([loadItems(), loadStats()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur validation');
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      toast.error('Motif obligatoire');
      return;
    }
    try {
      await agencyExpensesAPI.reject(rejectId, rejectReason.trim());
      toast.success('Frais rejete');
      setShowReject(false);
      setRejectReason('');
      setRejectId(null);
      await Promise.all([loadItems(), loadStats()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur rejet');
    }
  };

  const handleUploadInvoice = async (id, file) => {
    try {
      await agencyExpensesAPI.uploadInvoice(id, file);
      toast.success('Facture jointe');
      await loadItems();
      // refresh detail if open
      if (detail?.id === id) {
        const r = await agencyExpensesAPI.getById(id);
        setDetail(r.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur upload');
    }
  };

  const handleDownloadStatsPdf = async () => {
    try {
      const year = filterMonth ? filterMonth.slice(0, 4) : null;
      const r = await agencyExpensesAPI.downloadStatsPdf(year);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `frais_agence_${year || 'global'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur export PDF');
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="agency-expenses-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#1B3A4B] flex items-center gap-2">
            <Receipt className="h-8 w-8 text-[#D4A745]" />
            Frais d'agence
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {isAdmin
              ? 'Gestion complete des depenses de l\'agence (validation, modification, suppression)'
              : 'Soumettez vos frais avec facture jointe — validation par l\'administrateur'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setShowStats((s) => !s)}
            data-testid="toggle-stats-btn"
            className="border-[#1B3A4B] text-[#1B3A4B]"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            {showStats ? 'Masquer stats' : 'Stats'}
          </Button>
          <Button
            variant="outline"
            onClick={handleDownloadStatsPdf}
            data-testid="download-stats-pdf-btn"
            className="border-[#D4A745] text-[#D4A745] hover:bg-[#D4A745] hover:text-white"
          >
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
          <Button
            onClick={() => { setForm(EMPTY_FORM); setInvoiceFile(null); setShowCreate(true); }}
            data-testid="new-expense-btn"
            className="bg-[#1B3A4B] hover:bg-[#0d2937] text-white"
          >
            <Plus className="h-4 w-4 mr-2" /> Nouveau frais
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      {showStats && stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="stats-cards">
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase text-gray-500">Total</div>
            <div className="text-2xl font-bold text-[#1B3A4B]">{fmtMoney(stats.total)}</div>
            <div className="text-xs text-gray-500">{stats.count} frais</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase text-emerald-700">Valides</div>
            <div className="text-2xl font-bold text-emerald-700">{fmtMoney(stats.total_validated)}</div>
            <div className="text-xs text-gray-500">{stats.count_validated} frais</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase text-amber-700">En attente</div>
            <div className="text-2xl font-bold text-amber-700">{fmtMoney(stats.total_pending)}</div>
            <div className="text-xs text-gray-500">{stats.count_pending} frais</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase text-red-700">Rejetes</div>
            <div className="text-2xl font-bold text-red-700">{fmtMoney(stats.total_rejected)}</div>
            <div className="text-xs text-gray-500">{stats.count_rejected} frais</div>
          </CardContent></Card>
        </div>
      )}

      {showStats && stats && (stats.by_category?.length > 0 || stats.by_month?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stats.by_category?.length > 0 && (
            <Card><CardContent className="p-4">
              <h3 className="font-semibold text-[#1B3A4B] mb-3">Par categorie</h3>
              <div className="space-y-2">
                {stats.by_category.map((c) => (
                  <div key={c.category} className="flex justify-between text-sm border-b pb-1">
                    <span className="text-gray-700">{c.category}</span>
                    <span className="font-semibold text-[#1B3A4B]">{fmtMoney(c.amount)}</span>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}
          {stats.by_month?.length > 0 && (
            <Card><CardContent className="p-4">
              <h3 className="font-semibold text-[#1B3A4B] mb-3">Par mois</h3>
              <div className="space-y-2">
                {stats.by_month.map((m) => (
                  <div key={m.month} className="flex justify-between text-sm border-b pb-1">
                    <span className="text-gray-700">{m.month}</span>
                    <span className="font-semibold text-[#1B3A4B]">{fmtMoney(m.amount)}</span>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}
          {isAdmin && stats.by_agent?.length > 0 && (
            <Card className="md:col-span-2"><CardContent className="p-4">
              <h3 className="font-semibold text-[#1B3A4B] mb-3">Par agent</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {stats.by_agent.map((a) => (
                  <div key={a.agent} className="flex justify-between text-sm border-b pb-1">
                    <span className="text-gray-700">{a.agent}</span>
                    <span className="font-semibold text-[#1B3A4B]">{fmtMoney(a.amount)}</span>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          )}
        </div>
      )}

      {/* Filters */}
      <Card><CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs">Statut</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="validated">Valides</SelectItem>
              <SelectItem value="rejected">Rejetes</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Categorie</Label>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Mois</Label>
          <Input
            type="month"
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            data-testid="filter-month"
          />
        </div>
        <div className="flex items-end">
          <Button
            variant="outline"
            onClick={() => { setFilterStatus('all'); setFilterCategory('all'); setFilterMonth(''); }}
            className="w-full"
            data-testid="reset-filters-btn"
          >
            <Filter className="h-4 w-4 mr-2" /> Reinitialiser
          </Button>
        </div>
      </CardContent></Card>

      {/* List */}
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#1B3A4B] text-white">
              <tr>
                <th className="text-left p-3">Reference</th>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Categorie</th>
                <th className="text-left p-3">Description</th>
                <th className="text-right p-3">Montant</th>
                <th className="text-left p-3">Cree par</th>
                <th className="text-center p-3">Statut</th>
                <th className="text-center p-3">Facture</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="p-6 text-center text-gray-500">Chargement...</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-gray-500">
                  Aucun frais. Cliquez sur "Nouveau frais" pour commencer.
                </td></tr>
              )}
              {items.map((it) => {
                const Sb = STATUS_BADGES[it.status] || STATUS_BADGES.pending;
                const SIcon = Sb.icon;
                return (
                  <tr key={it.id} className="border-b hover:bg-gray-50" data-testid={`expense-row-${it.id}`}>
                    <td className="p-3 font-mono text-xs">{it.reference}</td>
                    <td className="p-3">{fmtDate(it.expense_date)}</td>
                    <td className="p-3">{it.category_label}</td>
                    <td className="p-3 max-w-xs truncate">{it.description}</td>
                    <td className="p-3 text-right font-semibold text-[#1B3A4B]">{fmtMoney(it.amount)}</td>
                    <td className="p-3 text-xs text-gray-600">{it.created_by_name}</td>
                    <td className="p-3 text-center">
                      <Badge className={`${Sb.cls} border`}>
                        <SIcon className="h-3 w-3 mr-1 inline" />{Sb.label}
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      {it.invoice ? (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${it.invoice.file_url}`}
                           target="_blank" rel="noreferrer"
                           className="text-[#D4A745] hover:underline inline-flex items-center"
                           data-testid={`view-invoice-${it.id}`}>
                          <FileText className="h-4 w-4 mr-1" /> Voir
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">Aucune</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost"
                                onClick={() => { setDetail(it); setShowDetail(true); }}
                                data-testid={`view-${it.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        {isAdmin && it.status === 'pending' && (
                          <>
                            <Button size="icon" variant="ghost"
                                    onClick={() => handleValidate(it.id)}
                                    title="Valider"
                                    data-testid={`validate-${it.id}`}
                                    className="text-emerald-600 hover:bg-emerald-50">
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost"
                                    onClick={() => { setRejectId(it.id); setRejectReason(''); setShowReject(true); }}
                                    title="Rejeter"
                                    data-testid={`reject-${it.id}`}
                                    className="text-red-600 hover:bg-red-50">
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {isAdmin && (
                          <>
                            <Button size="icon" variant="ghost"
                                    onClick={() => { setEditing({ ...it }); setShowEdit(true); }}
                                    data-testid={`edit-${it.id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost"
                                    onClick={() => handleDelete(it.id)}
                                    className="text-red-600 hover:bg-red-50"
                                    data-testid={`delete-${it.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {items.length > 0 && (
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td className="p-3" colSpan={4}>Totaux affiches</td>
                  <td className="p-3 text-right text-[#1B3A4B]">{fmtMoney(totals.all)}</td>
                  <td className="p-3" colSpan={4}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </CardContent></Card>

      {/* Create Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-expense-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1B3A4B]">Nouveau frais d'agence</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={form.expense_date}
                       onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                       data-testid="create-date" />
              </div>
              <div>
                <Label>Montant (FCFA) *</Label>
                <Input type="number" min="1" value={form.amount}
                       onChange={(e) => setForm({ ...form, amount: e.target.value })}
                       placeholder="Ex: 25000"
                       data-testid="create-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categorie *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="create-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Mode de paiement</Label>
                <Select value={form.payment_method}
                        onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger data-testid="create-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map((p) => (
                      <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea rows={2} value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="Ex: Achat de papier A4 et stylos pour le bureau"
                        data-testid="create-description" />
            </div>
            <div>
              <Label>Fournisseur (facultatif)</Label>
              <Input value={form.supplier}
                     onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                     placeholder="Ex: Papeterie Mali SARL"
                     data-testid="create-supplier" />
            </div>
            <div>
              <Label>Notes (facultatif)</Label>
              <Textarea rows={2} value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        data-testid="create-notes" />
            </div>
            <div>
              <Label>Facture jointe (PDF, JPG, PNG)</Label>
              <div className="flex items-center gap-2">
                <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                       onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                       data-testid="create-invoice-file" />
                {invoiceFile && (
                  <span className="text-xs text-emerald-700 flex items-center">
                    <Upload className="h-3 w-3 mr-1" /> {invoiceFile.name}
                  </span>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={submitting}
                    data-testid="submit-create-btn"
                    className="bg-[#1B3A4B] hover:bg-[#0d2937] text-white">
              {submitting ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal (admin) */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-2xl" data-testid="edit-expense-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1B3A4B]">Modifier le frais</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={editing.expense_date || ''}
                         onChange={(e) => setEditing({ ...editing, expense_date: e.target.value })} />
                </div>
                <div>
                  <Label>Montant (FCFA)</Label>
                  <Input type="number" value={editing.amount || 0}
                         onChange={(e) => setEditing({ ...editing, amount: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Categorie</Label>
                  <Select value={editing.category}
                          onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Mode de paiement</Label>
                  <Select value={editing.payment_method}
                          onValueChange={(v) => setEditing({ ...editing, payment_method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {paymentMethods.map((p) => (
                        <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={2} value={editing.description || ''}
                          onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div>
                <Label>Fournisseur</Label>
                <Input value={editing.supplier || ''}
                       onChange={(e) => setEditing({ ...editing, supplier: e.target.value })} />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={2} value={editing.notes || ''}
                          onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Annuler</Button>
            <Button onClick={handleEditSave} disabled={submitting}
                    data-testid="submit-edit-btn"
                    className="bg-[#1B3A4B] hover:bg-[#0d2937] text-white">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-xl" data-testid="detail-expense-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1B3A4B]">{detail?.reference}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Date</span><span>{fmtDate(detail.expense_date)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Categorie</span><span>{detail.category_label}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Montant</span><span className="font-bold text-[#1B3A4B]">{fmtMoney(detail.amount)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Mode de paiement</span><span>{detail.payment_method_label}</span></div>
              {detail.supplier && <div className="flex justify-between"><span className="text-gray-600">Fournisseur</span><span>{detail.supplier}</span></div>}
              <div><div className="text-gray-600">Description</div><div>{detail.description}</div></div>
              {detail.notes && <div><div className="text-gray-600">Notes</div><div className="whitespace-pre-wrap">{detail.notes}</div></div>}
              <div className="flex justify-between"><span className="text-gray-600">Cree par</span><span>{detail.created_by_name}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Cree le</span><span>{fmtDate(detail.created_at)}</span></div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Statut</span>
                <Badge className={`${(STATUS_BADGES[detail.status] || STATUS_BADGES.pending).cls} border`}>
                  {(STATUS_BADGES[detail.status] || STATUS_BADGES.pending).label}
                </Badge>
              </div>
              {detail.status === 'validated' && detail.validated_by_name && (
                <div className="flex justify-between"><span className="text-gray-600">Valide par</span><span>{detail.validated_by_name} le {fmtDate(detail.validated_at)}</span></div>
              )}
              {detail.status === 'rejected' && (
                <div className="bg-red-50 border border-red-200 rounded p-2">
                  <div className="text-red-800 font-semibold flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4" /> Motif de rejet
                  </div>
                  <div className="text-sm text-red-900">{detail.rejection_reason}</div>
                  {detail.validated_by_name && (
                    <div className="text-xs text-red-600 mt-1">Par {detail.validated_by_name} le {fmtDate(detail.validated_at)}</div>
                  )}
                </div>
              )}

              <div className="border-t pt-3">
                <div className="text-gray-600 text-xs uppercase mb-2">Facture</div>
                {detail.invoice ? (
                  <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${detail.invoice.file_url}`}
                       target="_blank" rel="noreferrer"
                       className="text-[#D4A745] hover:underline flex items-center">
                      <FileText className="h-4 w-4 mr-1" /> {detail.invoice.file_name || 'Facture'}
                    </a>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">Aucune facture jointe.</div>
                )}
                {(isAdmin || (detail.created_by_id === user?.id && detail.status === 'pending')) && (
                  <div className="mt-2">
                    <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                           onChange={(e) => {
                             const f = e.target.files?.[0];
                             if (f) handleUploadInvoice(detail.id, f);
                           }}
                           data-testid="detail-upload-invoice" />
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetail(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent className="max-w-md" data-testid="reject-modal">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center">
              <XCircle className="h-5 w-5 mr-2" /> Rejeter le frais
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motif du rejet *</Label>
            <Textarea rows={4} value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Ex: Facture illisible, montant incoherent, doublon..."
                      data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReject(false)}>Annuler</Button>
            <Button onClick={handleRejectSubmit} className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="confirm-reject-btn">
              Confirmer le rejet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
